import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, MenuItem, Select, FormControl,
  InputLabel, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Tabs, Tab, CircularProgress, Snackbar, Alert,
  Switch, FormControlLabel, Tooltip
} from "@mui/material";
import { Edit, Delete, Add, Refresh } from "@mui/icons-material";
import axios from "axios";

const API_BASE = "https://api.bharatdarshan.app/api/app/services/admin";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

const SECTIONS = ["all", "sanatani", "kutumb", "rojgar", "postbanao", "spl_live", "gyan_shala"];
const SECTION_OPTIONS = ["sanatani", "kutumb", "rojgar", "postbanao", "spl_live", "gyan_shala"];

// 15 predefined icons for service plans
const PREDEFINED_ICONS = [
  { emoji: "🙏", label: "Namaste" },
  { emoji: "⭐", label: "Star" },
  { emoji: "🕉️", label: "Om" },
  { emoji: "🔔", label: "Bell" },
  { emoji: "🪔", label: "Diya" },
  { emoji: "📿", label: "Mala" },
  { emoji: "🛕", label: "Mandir" },
  { emoji: "🌺", label: "Flower" },
  { emoji: "💎", label: "Diamond" },
  { emoji: "👑", label: "Crown" },
  { emoji: "🎯", label: "Target" },
  { emoji: "🏆", label: "Trophy" },
  { emoji: "💰", label: "Money" },
  { emoji: "📱", label: "Mobile" },
  { emoji: "🎓", label: "Education" },
];

const defaultForm = {
  name: "", nameHindi: "", description: "", descriptionHindi: "",
  price: "", section: "sanatani", icon: "", color: "#FF9800", comingSoon: false,
  imageUrl: "", duration: "1 month", features: "", isActive: true, sortOrder: 0
};

const ServicePlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plan: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const currentSection = SECTIONS[tabValue];

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const params = currentSection !== "all" ? `?section=${currentSection}` : "";
      const res = await axios.get(`${API_BASE}/all${params}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        setPlans(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      setSnackbar({ open: true, message: "Failed to load plans", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, [tabValue]);

  const handleOpenAdd = () => {
    setEditingPlan(null);
    setForm(defaultForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name || "",
      nameHindi: plan.nameHindi || "",
      description: plan.description || "",
      descriptionHindi: plan.descriptionHindi || "",
      price: plan.price || "",
      section: plan.section || "sanatani",
      comingSoon: plan.comingSoon || false,
      icon: plan.icon || "",
      color: plan.color || "#FF9800",
      imageUrl: plan.imageUrl || "",
      duration: plan.duration || "1 month",
      features: Array.isArray(plan.features) ? plan.features.join(", ") : (plan.features || ""),
      isActive: plan.isActive !== false,
      sortOrder: plan.sortOrder || 0
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price || !form.section) {
      setSnackbar({ open: true, message: "Name, Price and Section are required", severity: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        sortOrder: Number(form.sortOrder),
        features: form.features ? form.features.split(",").map(f => f.trim()).filter(Boolean) : []
      };
      if (editingPlan) {
        await axios.put(`${API_BASE}/${editingPlan._id}`, payload, { headers: getAuthHeaders() });
        setSnackbar({ open: true, message: "Plan updated successfully", severity: "success" });
      } else {
        await axios.post(`${API_BASE}/add`, payload, { headers: getAuthHeaders() });
        setSnackbar({ open: true, message: "Plan created successfully", severity: "success" });
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (err) {
      console.error("Error saving plan:", err);
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to save plan", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const plan = deleteDialog.plan;
    if (!plan) return;
    try {
      await axios.delete(`${API_BASE}/${plan._id}`, { headers: getAuthHeaders() });
      setSnackbar({ open: true, message: "Plan deactivated successfully", severity: "success" });
      setDeleteDialog({ open: false, plan: null });
      fetchPlans();
    } catch (err) {
      console.error("Error deleting plan:", err);
      setSnackbar({ open: true, message: "Failed to delete plan", severity: "error" });
    }
  };

  const getSectionColor = (section) => {
    switch (section) {
      case "sanatani": return "#FF9800";
      case "kutumb": return "#4CAF50";
      case "rojgar": return "#2196F3";
      case "postbanao": return "#9C27B0";
      case "spl_live": return "#E91E63";
      case "gyan_shala": return "#00BCD4";
      default: return "#757575";
    }
  };

  // Get display icon/image for a plan
  const renderPlanIcon = (plan) => {
    if (plan.imageUrl) {
      return (
        <Box
          component="img"
          src={plan.imageUrl}
          alt={plan.name}
          sx={{ width: 40, height: 40, borderRadius: 1, objectFit: "cover" }}
        />
      );
    }
    return (
      <Box sx={{ fontSize: 28, lineHeight: 1 }}>
        {plan.icon || "🙏"}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", color: "#FF9800" }}>Service Plans</Typography>
        <Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchPlans} sx={{ mr: 1 }}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}
            sx={{ bgcolor: "#FF9800", "&:hover": { bgcolor: "#F57C00" } }}>Add New Plan</Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto"
          sx={{ "& .MuiTab-root": { textTransform: "capitalize", fontWeight: "bold" } }}>
          {SECTIONS.map((s) => (
            <Tab key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} />
          ))}
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: "#FF9800" }}>
              <TableRow>
                {["Icon", "Name", "Name Hindi", "Price", "Section", "Duration", "Features", "Active", "Sort", "Actions"].map((h) => (
                  <TableCell key={h} sx={{ color: "white", fontWeight: "bold" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <Typography color="textSecondary">No plans found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow key={plan._id} hover sx={{ opacity: plan.isActive === false ? 0.5 : 1 }}>
                    <TableCell>{renderPlanIcon(plan)}</TableCell>
                    <TableCell><Typography fontWeight="bold">{plan.name || "N/A"}</Typography></TableCell>
                    <TableCell>{plan.nameHindi || "\u2014"}</TableCell>
                    <TableCell><Typography fontWeight="bold" color="primary">{"\u20B9"}{plan.price || 0}</Typography></TableCell>
                    <TableCell>
                      <Chip label={plan.section || "N/A"} size="small"
                        sx={{ bgcolor: getSectionColor(plan.section), color: "white" }} />
                    </TableCell>
                    <TableCell>{plan.duration || "\u2014"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {Array.isArray(plan.features) ? plan.features.slice(0, 3).map((f, i) => (
                          <Chip key={i} label={f} size="small" variant="outlined" />
                        )) : null}
                        {Array.isArray(plan.features) && plan.features.length > 3 && (
                          <Chip label={`+${plan.features.length - 3}`} size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={plan.isActive !== false ? "Active" : "Inactive"}
                        color={plan.isActive !== false ? "success" : "default"} size="small" />
                    </TableCell>
                    <TableCell>{plan.sortOrder || 0}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit"><IconButton color="primary" onClick={() => handleOpenEdit(plan)}><Edit /></IconButton></Tooltip>
                      <Tooltip title="Deactivate"><IconButton color="error" onClick={() => setDeleteDialog({ open: true, plan })}><Delete /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#FF9800", color: "white" }}>
          {editingPlan ? "Edit Plan" : "Add New Plan"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name *" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name Hindi" value={form.nameHindi}
                onChange={(e) => setForm({ ...form, nameHindi: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline rows={3} label="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline rows={3} label="Description Hindi" value={form.descriptionHindi}
                onChange={(e) => setForm({ ...form, descriptionHindi: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Price *" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Section *</InputLabel>
                <Select value={form.section} label="Section *"
                  onChange={(e) => setForm({ ...form, section: e.target.value })}>
                  {SECTION_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Duration" placeholder="e.g. 1 month, 1 year"
                value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
            </Grid>

            {/* Icon Selector - 15 predefined icons */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "#555" }}>
                Select Icon (click to choose)
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {PREDEFINED_ICONS.map((iconObj) => (
                  <Tooltip key={iconObj.emoji} title={iconObj.label}>
                    <Box
                      onClick={() => setForm({ ...form, icon: iconObj.emoji })}
                      sx={{
                        width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 28, borderRadius: 2, cursor: "pointer",
                        border: form.icon === iconObj.emoji ? "3px solid #FF9800" : "2px solid #e0e0e0",
                        bgcolor: form.icon === iconObj.emoji ? "#FFF3E0" : "#fafafa",
                        transition: "all 0.2s",
                        "&:hover": { borderColor: "#FF9800", bgcolor: "#FFF8E1", transform: "scale(1.1)" },
                      }}
                    >
                      {iconObj.emoji}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
              <TextField
                size="small"
                label="Or type custom icon/emoji"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                sx={{ width: 250 }}
                helperText={form.icon ? `Selected: ${form.icon}` : "Click above or type here"}
              />
            </Grid>

            {/* Image URL - if provided, image shows instead of icon */}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Image URL (overrides icon)" value={form.imageUrl}
                onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                helperText="If image URL is provided, it will be shown instead of the icon"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Color" type="color" value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>

            {/* Image Preview */}
            {form.imageUrl && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#555" }}>Image Preview:</Typography>
                <Box
                  component="img"
                  src={form.imageUrl}
                  alt="Preview"
                  sx={{ width: 80, height: 80, borderRadius: 2, objectFit: "cover", border: "2px solid #e0e0e0" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField fullWidth label="Features (comma-separated)" placeholder="Feature 1, Feature 2, Feature 3"
                value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} />
              {form.features && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                  {form.features.split(",").map((f, i) => f.trim() && (
                    <Chip key={i} label={f.trim()} size="small" color="primary" variant="outlined" />
                  ))}
                </Box>
              )}
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="number" label="Sort Order" value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Switch checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })} color="success" />}
                label="Active" sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={<Switch checked={form.comingSoon}
                  onChange={(e) => setForm({ ...form, comingSoon: e.target.checked })} color="warning" />}
                label="Coming Soon" sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: "#FF9800", "&:hover": { bgcolor: "#F57C00" } }}>
            {saving ? <CircularProgress size={24} color="inherit" /> : (editingPlan ? "Update" : "Create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, plan: null })}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to deactivate <b>{deleteDialog.plan?.name}</b>?</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This will set the plan as inactive (soft delete).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, plan: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServicePlans;
